import { useState } from 'react';

/**
 * Hook para manejo de estado de formularios de autenticación
 *
 * Usado por Login y Register. Proporciona estado, validaciones y handlers.
 *
 * @param {Object} initialData - Datos iniciales del formulario
 * @returns {Object} Estado y handlers del formulario
 * @returns {Object} returns.formData - Datos del formulario
 * @returns {Object} returns.errors - Errores de validación
 * @returns {boolean} returns.isLoading - Estado de carga
 * @returns {Function} returns.handleChange - Handler para inputs
 * @returns {Function} returns.validateEmail - Validador de email
 * @returns {Function} returns.validatePassword - Validador de password
 */
export const useAuthForm = (initialData) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /**
   * Maneja cambios en inputs del formulario
   * @param {Event} e - Evento del input
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  /**
   * Valida formato de email
   * @param {string} email - Email a validar
   * @returns {string|null} Mensaje de error o null si válido
   */
  const validateEmail = (email) => {
    if (!email) return 'El email es requerido';
    if (!/\S+@\S+\.\S+/.test(email)) return 'Email invalido';
    return null;
  };

  /**
   * Valida formato de contraseña
   * @param {string} password - Contraseña a validar
   * @returns {string|null} Mensaje de error o null si válida
   */
  const validatePassword = (password) => {
    if (!password) return 'La contrasena es requerida';
    if (password.length < 6) return 'Minimo 6 caracteres';
    return null;
  };

  /**
   * Valida que confirmación coincida con contraseña
   * @param {string} password - Contraseña original
   * @param {string} confirmPassword - Confirmación
   * @returns {string|null} Mensaje de error o null si coinciden
   */
  const validateConfirmPassword = (password, confirmPassword) => {
    if (!confirmPassword) return 'Confirma tu contrasena';
    if (password !== confirmPassword) return 'Las contrasenas no coinciden';
    return null;
  };

  /** Alterna visibilidad de campo contraseña */
  const togglePassword = () => setShowPassword(!showPassword);
  /** Alterna visibilidad de campo confirmar contraseña */
  const toggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    isLoading,
    setIsLoading,
    showPassword,
    togglePassword,
    showConfirmPassword,
    toggleConfirmPassword,
    handleChange,
    validateEmail,
    validatePassword,
    validateConfirmPassword
  };
};

export default useAuthForm;
